import React, { useEffect, useState } from 'react';
import { Button, Card, Container, Table } from 'react-bootstrap';
import { Icon } from '@lomarengas/design-system';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import queryString from 'query-string';
import { Helmet } from 'react-helmet';
import { InputCheck, Loader, SectionHeader } from '../../common/components';
import { SeasonRateListItem } from './Components/SeasonRateListItem';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  deleteSeasonRatesByAccommodationIdAsync,
  getSeasonRatesAsync,
  selectSeasonRateItems,
  selectSeasonRateItemsLoadingStatus,
  deleteSeasonRateAsync,
} from './seasonRatesSlice';
import { LOADING_STATUS, PERMISSIONS } from '../../common/constants';
import { checkPermission } from '../auth/authSlice';
import { AccommodationSelector } from '../../common/components/AccommodationSelector';
import { copySeasonRatesFromAccommodationToAnother } from '../seasonRateTools/rateToolsSlice';

function EditSeasonRates({
  writePermissions,
}: {
  writePermissions?: Array<PERMISSIONS>;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const parsedQuery = queryString.parse(location.search.replace('?', ''));
  const dispatch = useAppDispatch();
  const seasonRateItems = useAppSelector(selectSeasonRateItems);
  const seasonRateItemsLoadingStatus = useAppSelector(
    selectSeasonRateItemsLoadingStatus,
  );
  const [selectedAccommodationId, setSelectedAccommodationId] =
    useState<string>('');
  const [selectedAccommodationName, setSelectedAccommodationName] =
    useState<string>('');
  const hasWriteAccess = useAppSelector(checkPermission(writePermissions));
  const [
    selectedAccommodationIdForSeasonCopy,
    setSelectedAccommodationIdForSeasonCopy,
  ] = useState<string | null>(null);
  const [showPastSeasonRates, setShowPastSeasonRates] = useState(false);
  const [copyPrices, setCopyPrices] = useState(true);
  const [copyRules, setCopyRules] = useState(true);
  const [useRulePrices, setUseRulePrices] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Get accommodation info from url params, fetch default rate by accommodation id
  useEffect(() => {
    let accommodationName = '';
    if (parsedQuery.accommodation && parsedQuery.accommodation !== '') {
      accommodationName = decodeURI(parsedQuery.accommodation.toString());
    }

    setSelectedAccommodationId(params.accommodationId);
    setSelectedAccommodationName(accommodationName);
  }, [params.accommodationId, parsedQuery.accommodation]);

  // Use component's default rate to fetch season rate only if accommodation matches default rate
  useEffect(() => {
    if (selectedAccommodationId) {
      dispatch(
        getSeasonRatesAsync({ accommodationId: selectedAccommodationId }),
      );
      // Clear any previous selections when accommodation changes
      setSelectedIds(new Set());
    }
  }, [dispatch, selectedAccommodationId]);

  const handleAccommodationForSeasonCopySelected = (
    accommodationId: string,
  ) => {
    setSelectedAccommodationIdForSeasonCopy(accommodationId);
  };

  const handleToggleSelect = (id: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const deleteSelectedSeasonRates = async () => {
    if (selectedIds.size === 0) return;
    const confirmDelete = window.confirm(
      'Haluatko varmasti poistaa valitut kaudet?',
    );
    if (!confirmDelete) return;
    setIsDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      await Promise.all(
        ids.map(id => dispatch(deleteSeasonRateAsync(id)).unwrap()),
      );
      await dispatch(
        getSeasonRatesAsync({ accommodationId: selectedAccommodationId }),
      ).unwrap();
      setSelectedIds(new Set());
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSeasonRateCopyForAccommodation = async () => {
    if (selectedAccommodationIdForSeasonCopy) {
      setIsImporting(true);
      dispatch(
        copySeasonRatesFromAccommodationToAnother({
          fromAccommodationId: selectedAccommodationIdForSeasonCopy,
          toAccommodationId: selectedAccommodationId,
          copyPrices,
          copyRules,
          useRulePrices,
        }),
      )
        .unwrap()
        .then(() => {
          dispatch(
            getSeasonRatesAsync({ accommodationId: selectedAccommodationId }),
          );
        })
        .finally(() => {
          setIsImporting(false);
        });
    }
  };

  const toggleShowPastSeasonRates = () => {
    setShowPastSeasonRates(!showPastSeasonRates);
  };

  const deleteAllSeasonRates = async () => {
    if (selectedAccommodationId) {
      const confirmDelete = window.confirm(
        'Haluatko varmasti poistaa kohteen kaikki kaudet?',
      );
      if (!confirmDelete) return;

      setIsDeleting(true);

      await dispatch(
        deleteSeasonRatesByAccommodationIdAsync(selectedAccommodationId),
      ).unwrap();
      dispatch(
        getSeasonRatesAsync({ accommodationId: selectedAccommodationId }),
      );
      setIsDeleting(false);
    }
  };

  const currentDate = new Date();
  const filteredSeasonRateItems = seasonRateItems?.filter(item => {
    const endDate = new Date(item.endDate);
    return showPastSeasonRates || endDate >= currentDate;
  });

  // Add new season rate and copy rates button elements
  const HeaderButtons: JSX.Element = hasWriteAccess && (
    <>
      <Button onClick={toggleShowPastSeasonRates} className="me-2">
        {showPastSeasonRates
          ? 'Piilota menneet hintakaudet'
          : 'Näytä menneet hintakaudet'}
      </Button>
      <Link
        to={`/rates/addseasonrate/${selectedAccommodationId}?accommodation=${selectedAccommodationName}`}
        className="btn btn-blue ps-16"
      >
        <Icon CssClass="me-8" AriaLabel="Uusi" Name="plus" />
        Lisää uusi
      </Link>
    </>
  );

  // Map table items to JSX elements
  const renderSeasonRateItems = filteredSeasonRateItems?.map((item, index) => (
    <SeasonRateListItem
      key={item.id}
      AccommodationName={selectedAccommodationName}
      AccommodationId={item.accommodationId}
      SeasonRateItem={item}
      hasWriteAccess={hasWriteAccess}
      selectable={hasWriteAccess}
      isSelected={selectedIds.has(item.id)}
      onToggleSelect={handleToggleSelect}
    />
  ));

  return (
    <>
      <Helmet>
        <title>
          Hinnoittelu - Sesonkihintakausien muokkaus -{' '}
          {selectedAccommodationName}
        </title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          BackLink={() => navigate(-1)}
          Breadcrumbs={['Hinnoittelu', 'Sesonkihintakaudet']}
          Children={HeaderButtons}
          Title={`Muokkaa sesonkihintakausia: ${selectedAccommodationName}`}
        />
        <div className="d-md-flex align-items-start justify-content-between mb-24">
          <div>
            <Link
              to={`/rates/defaultrate/${selectedAccommodationId}?accommodation=${selectedAccommodationName}`}
            >
              Siirry oletushintatyökaluun
            </Link>
            <br />
            <Link to={`/accommodations/details/${selectedAccommodationId}`}>
              Siirry kohteen tietoihin
            </Link>
          </div>
          {filteredSeasonRateItems.length > 0 && (
            <div className="d-flex gap-8 mt-16 mt-md-0">
              {selectedIds.size > 0 ? (
                <Button
                  onClick={deleteSelectedSeasonRates}
                  className="ms-md-8"
                  variant="danger"
                  disabled={isDeleting}
                  size="sm"
                >
                  {isDeleting
                    ? 'Poistetaan kausia'
                    : `Poista valitut kaudet (${selectedIds.size})`}
                </Button>
              ) : (
                <Button
                  onClick={deleteAllSeasonRates}
                  className="ms-md-8"
                  variant="danger"
                  disabled={isDeleting}
                  size="sm"
                >
                  {isDeleting ? 'Poistetaan kausia' : 'Poista kaikki kaudet'}
                </Button>
              )}
            </div>
          )}
        </div>

        <Loader
          IsLoading={seasonRateItemsLoadingStatus === LOADING_STATUS.LOADING}
        />

        {filteredSeasonRateItems !== null &&
          seasonRateItemsLoadingStatus === LOADING_STATUS.IDLE && (
            <>
              {filteredSeasonRateItems.length > 0 && (
                <Card className="border shadow-none mt-48">
                  <div className="table-responsive-lg">
                    <Table className="table-data">
                      <thead>
                        <tr>
                          <th style={{ width: '56px' }} />
                          <th>Nimi</th>
                          <th>Aika</th>
                          <th>Vrk säännöt</th>
                          <th>Äkkilähtö / Lisäp.</th>
                          <th>Checkin / Checkout</th>
                          <th>Vkp. hinnat</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>{renderSeasonRateItems}</tbody>
                    </Table>
                  </div>
                </Card>
              )}
              {seasonRateItems.length < 1 && (
                <div className="my-48">
                  <div
                    className="border bg-white rounded shadow-lg mx-auto p-24 p-xl-32"
                    style={{ maxWidth: 640 }}
                  >
                    <p>Ei sesonkihintakausia valitulla kohteella.</p>
                    <AccommodationSelector
                      Subtitle="Valitse kohde jolta haluat tuoda hintakaudet."
                      Title="Tuo hintakaudet toiselta kohteelta"
                      onAccommodationSelected={
                        handleAccommodationForSeasonCopySelected
                      }
                    />
                    {selectedAccommodationIdForSeasonCopy && (
                      <div className="mt-16">
                        <InputCheck
                          Id="copyPrices"
                          Name="copyPrices"
                          Label="Tuo hinnat"
                          HandleChange={e => {
                            const { checked } = e.target;
                            setCopyPrices(checked);
                            if (!checked) {
                              setUseRulePrices(false);
                            }
                          }}
                          Checked={copyPrices}
                          IsRequired={false}
                        />
                        <InputCheck
                          Id="useRulePrices"
                          Name="useRulePrices"
                          Label="Käytä korotettuja hintoja"
                          HandleChange={e => setUseRulePrices(e.target.checked)}
                          Checked={useRulePrices}
                          IsRequired={false}
                          IsDisabled={!copyPrices}
                        />
                        <InputCheck
                          Id="copyRules"
                          Name="copyRules"
                          Label="Tuo säännöt"
                          HandleChange={e => setCopyRules(e.target.checked)}
                          Checked={copyRules}
                          IsRequired={false}
                        />
                        <Button
                          variant="primary"
                          className="w-100"
                          onClick={handleSeasonRateCopyForAccommodation}
                          disabled={isImporting}
                        >
                          {isImporting ? 'Tuodaan kausia...' : 'Tuo kaudet'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
      </Container>
    </>
  );
}

export default EditSeasonRates;
